import { parseStretch, parseRepeat } from "../../../model/millingStretch";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import millingApi from "../../../data/api/millingApi";
import api from "../../../data/api/mainApi";
import { useLanguage } from "../../helpers/LanguageContext";

export const emptyMilling = () => ({ key: crypto.randomUUID(), name: "", active: true, materialType: "MDF", imageKey: "", variants: [] });
export function validateMilling(milling) {
  if (!milling.name.trim()) throw new Error("A marás neve kötelező.");
  for (const value of [milling,...milling.variants]) {
    parseRepeat(value.repeatX); parseRepeat(value.repeatY);
    parseStretch(value.stretchX); parseStretch(value.stretchY);
    if (value.selectionMode && value.selectionMode !== "manual") {
      const { minSizeMm: min, maxSizeMm: max } = value;
      if ((min == null && max == null) ||
          (min != null && (!Number.isFinite(min) || min < 0 || min > 10000)) ||
          (max != null && (!Number.isFinite(max) || max <= 0 || max > 10000)) ||
          (min != null && max != null && min >= max))
        throw new Error("Adj meg érvényes méretkorlátot a változathoz: alsó határ ≤ méret < felső határ (mm, legfeljebb 10000).");
    }
    if(value.imageWidthMm != null || value.imageHeightMm != null) {
      if(![value.imageWidthMm,value.imageHeightMm].every(n=>Number.isFinite(n)&&n>0&&n<=10000))
        throw new Error("A kép eredeti szélességét és magasságát is add meg pozitív milliméterértékkel (legfeljebb 10000).");
    }
  }
  const names = milling.variants.map((v) => v.name.trim().toLocaleLowerCase());
  if (names.some((name) => !name) || new Set(names).size !== names.length) throw new Error("A változatok neve legyen kitöltve és különböző.");
}
export default function useMillingsPageController() {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const millings = useSelector((state) => state.millings || []);
  const [drafts, setDrafts] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  useEffect(() => {
    let active = true;
    millingApi.getAllMillingsApi().catch(() => { if (active) setError("A marások betöltése nem sikerült."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  const open = (milling) => {
    setError("");
    setDrafts(milling ? [{ ...milling, key: String(milling.millingId), variants: (milling.variants || []).map((v) => ({ ...v })) }] : [emptyMilling()]);
  };
  const save = useCallback(async () => {
    if (saving.current) return;
    try { drafts.forEach(validateMilling); } catch (e) { setError(e.message); return; }
    saving.current = true; setBusy(true); setError("");
    let saved = 0;
    try {
      for (const draft of drafts) {
        const upload = async (value) => {
          let uploaded = value;
          if (value.file) {
            const data = new FormData(); data.append("image", value.file);
            const response = await api.post("/millings/images", data, { headers: { "Content-Type": "multipart/form-data" } });
            uploaded = { ...uploaded, imageKey: response.data.imageKey, file: null };
          }
          if (value.dxfFile) {
            const data = new FormData(); data.append("file", value.dxfFile);
            const response = await api.post("/millings/dxf", data, { headers: { "Content-Type": "multipart/form-data" } });
            uploaded = { ...uploaded, dxfKey: response.data.dxfKey, dxfFile: null };
          }
          return uploaded;
        };
        let ready = await upload(draft);
        setDrafts((rows) => rows.map((r) => r.key === draft.key ? ready : r));
        const variants = [];
        for (const variant of ready.variants) {
          const uploaded = await upload(variant);
          variants.push(uploaded);
          ready = { ...ready, variants: ready.variants.map((v) => v.variantId === uploaded.variantId ? uploaded : v) };
          const snapshot = ready;
          setDrafts((rows) => rows.map((r) => r.key === draft.key ? snapshot : r));
        }
        const payload = { name: ready.name.trim(), materialType: ready.materialType, active: ready.active,
          imageKey: ready.imageKey || "", dxfKey: ready.dxfKey || "",
          repeatX: ready.repeatX || "", repeatY: ready.repeatY || "",
          stretchX: ready.stretchX || "", stretchY: ready.stretchY || "",
          imageWidthMm: ready.imageWidthMm || null, imageHeightMm: ready.imageHeightMm || null,
          variants: variants.map(({ variantId, name, imageKey, dxfKey, imageWidthMm, imageHeightMm, stretchX, stretchY, repeatX, repeatY, selectionMode, minSizeMm, maxSizeMm }) => ({ variantId, repeatX: repeatX || "", repeatY: repeatY || "", stretchX: stretchX || "", stretchY: stretchY || "", selectionMode: selectionMode || "manual", minSizeMm: minSizeMm ?? null, maxSizeMm: maxSizeMm ?? null, name: name.trim(), imageKey: imageKey || "", dxfKey: dxfKey || "", imageWidthMm: imageWidthMm || null, imageHeightMm: imageHeightMm || null })) };
        if (ready.millingId) await dispatch(millingApi.updateMillingApi(ready.millingId, payload));
        else await dispatch(millingApi.createMillingApi(payload));
        saved += 1;
        setDrafts((rows) => rows.filter((r) => r.key !== draft.key));
      }
      setDrafts(null);
    } catch (e) {
      const reason = e.response?.data?.message;
      setError(`${saved ? `${saved} marás mentve. ` : ""}A mentés nem sikerült. ${typeof reason === "string" ? `${reason} ` : ""}A megmaradt sorokat újra mentheted.`);
    } finally { saving.current = false; setBusy(false); }
  }, [drafts, dispatch]);
  const remove = async (milling) => {
    if (saving.current) return;
    saving.current = true; setBusy(true); setError("");
    try {
      await dispatch(millingApi.deleteMillingApi(milling.millingId));
      setDrafts(null);
      return true;
    } catch (e) {
      setError(e.response?.data?.message || "A marás törlése nem sikerült. Próbáld újra.");
      return false;
    } finally { saving.current = false; setBusy(false); }
  };
  const toggleActive = async (milling) => {
    if (saving.current) return;
    saving.current = true; setBusy(true); setError("");
    const active = !milling.active;
    try {
      await dispatch(millingApi.updateMillingApi(milling.millingId, { active }));
      setDrafts((rows) => rows?.map((row) => row.key === milling.key ? { ...row, active } : row));
    } catch (e) {
      setError(e.response?.data?.message || "Az állapot módosítása nem sikerült. Próbáld újra.");
    } finally { saving.current = false; setBusy(false); }
  };
  return { t, millings, drafts, setDrafts, error, loading, busy, open, save, remove, toggleActive };
}
